// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { chargeSchema } from '@/billing/api/types';
import { Currency } from '@/currency/api/types';
import { serializeCurrencyForAPINullable } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const markChargeBillableResponseSchema = z
  .object({
    data: z.object({
      updatedCharge: chargeSchema,
      newCharge: chargeSchema.nullable(),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    updatedCharge: data.updatedCharge,
    newCharge: data.newCharge,
  }));

type MarkChargeBillableResponse = z.infer<
  typeof markChargeBillableResponseSchema
>;

export interface MarkChargeBillableInput {
  chargeKey: string;
  // Stamped onto billing_eligible_date, the moment the charge actually became
  // eligible to bill. This does not change the charge's schedule
  // (expected_billable_date).
  billingEligibleDate: DateTime;
  quantity: number;
  // Only sent on the FIXED split path (quantity < currentQty). Pass null
  // otherwise — the backend rejects an override on the no-split path because
  // there is no leftover slice to absorb the price change.
  totalPriceOverride: Currency | null;
  // Required when activating a MILESTONE charge — the BE has no service
  // period stamped on the row at generation time and uses these to fill
  // in the activated phase's service window. Ignored for PERIODIC.
  servicePeriodStart: DateTime | null;
  servicePeriodEnd: DateTime | null;
}

export async function markChargeBillable(
  input: MarkChargeBillableInput
): Promise<MarkChargeBillableResponse> {
  const url = `/v1/charge/${input.chargeKey}/mark-billable`;
  const response = await api.post(url, {
    billingEligibleDate: serializeDateForAPI(input.billingEligibleDate),
    quantity: input.quantity,
    totalPriceOverride: serializeCurrencyForAPINullable(
      input.totalPriceOverride
    ),
    servicePeriodStart: serializeDateForAPI(input.servicePeriodStart),
    servicePeriodEnd: serializeDateForAPI(input.servicePeriodEnd),
  });

  return parseResponse(markChargeBillableResponseSchema, response.data, {
    schemaName: 'MarkChargeBillable',
    endpoint: url,
  });
}
